import { createApi } from "@reduxjs/toolkit/query/react";
import fetchAllPages from "../../utils/fetchAllPages";
import { catalogRow, offsetTypeRow, withCatalogRows, type CatalogRow } from "./catalogRows";
import { retryUnlessClientError } from "../retryConditions";
import { createRetryingBaseQuery, DEFAULT_MAX_RETRIES } from "../retryingBaseQuery";

// A 4xx here (a role that may not see the catalogue, a definition someone else
// deleted) is the same on every attempt, so it is shown at once.
const baseQuery = createRetryingBaseQuery(
  process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "",
  DEFAULT_MAX_RETRIES,
  retryUnlessClientError,
);

// A catalogue fetched page by page, with its rows in the shape the screens read: the FlatBuffer
// API sends `UuidT` ids and an offset type's attributes as a JSON string (see catalogRows.ts).
async function catalogue(
  pages: ReturnType<typeof fetchAllPages>,
  toRow: (row: Record<string, unknown>) => CatalogRow,
) {
  const result = await pages;
  return result.error ? result : { ...result, data: withCatalogRows(result.data, toRow) };
}

export const configurationFileAPI = createApi({
  reducerPath: "configurationFileAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: [
    "tagGetConfigurationFiles",
    "tagPostConfigurationFile",
    "tagPutConfigurationFile",
    "tagDeleteConfigurationFile",
    "tagGetAllOffsetsTypes",
    "tagGetSpecialSwitchesCatalog",
    "tagGetSpecialSwitches",
    "tagGetDataBlocks",
    "tagGetDataBlocksTypes",
    "tagGetVendors",
    "tagGetOffsetsByDataBlock",
  ],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ONE FULL CONFIGURATION */
    getConfiguration: builder.query({
      query: (body: any) => ({
        url: `/configurations/${body.configurationId}`,
        headers: { authorization: body.token },
      }),
      providesTags: [],
    }),
    //* *********************************************************** */
    //* GET ALL CONFIGURATION FILES */
    getConfigurationFiles: builder.query({
      query: (body: any) => ({
        url: `/configurations/?page=${body.page}&limit=${body.limit}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetConfigurationFiles"],
    }),
    //* *********************************************************** */
    //* GET ALL OFFSET TYPES */
    getAllOffsetsTypes: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        catalogue(
          fetchAllPages(_baseQuery, `/offset-type/?page=1&limit=500`, {
            authorization: body.token,
          }),
          offsetTypeRow,
        ),
      providesTags: ["tagGetAllOffsetsTypes"],
    }),
    //* *********************************************************** */
    //* GET OFFSET BY DATA BLOCK */
    getOffsetsByDataBlock: builder.query({
      query: (body: any) => ({
        url: `/data-blocks/offsets/${body.dataBlockID}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetOffsetsByDataBlock"],
    }),
    //* *********************************************************** */
    //* CREATE OFFSET TYPE */
    postOffsetType: builder.mutation({
      query: (body: any) => ({
        url: `/offset-type/`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetAllOffsetsTypes"],
    }),
    //* *********************************************************** */
    //* DELETE OFFSET TYPE BY DATA BLOCK */
    deleteOffsetTypeByDataBlock: builder.mutation({
      query: (body: any) => ({
        url: `/data-blocks/offsets/`,
        method: "DELETE",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetConfigurationFiles", "tagGetOffsetsByDataBlock"],
    }),
    //* *********************************************************** */
    //* DELETE OFFSET TYPE */
    deleteOffsetType: builder.mutation({
      query: (body: any) => ({
        url: `/offset-type/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetAllOffsetsTypes"],
    }),
    //* *********************************************************** */
    //* UPDATE OFFSET TYPE */
    putOffsetType: builder.mutation({
      query: (body: any) => ({
        url: `/offset-type/${body.id}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetAllOffsetsTypes"],
    }),
    //* *********************************************************** */
    //* CREATE CONFIGURATION FILE */
    postConfiguration: builder.mutation({
      query: (body: any) => ({
        url: "/configurations/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateConfiguration" },
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* CREATE CONFIGURATION FILE COMPLETE */
    postConfigurationFile: builder.mutation({
      query: (body: any) => ({
        url: "/configurations/complete",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetConfigurationFiles"],
    }),
    //* *********************************************************** */
    //* POST DEFINITION */
    postDefinition: builder.mutation({
      query: (body: any) => ({
        url: `/data-blocks/definitions/${body.id}`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),

    //* *********************************************************** */
    //* UPDATE DEFINITION */
    putDefinition: builder.mutation({
      query: (body: any) => ({
        url: `/data-blocks/definitions/${body.id}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* DELETE DEFINITION */
    deleteDefinition: builder.mutation({
      query: (body: any) => ({
        url: `/definitions/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* CREATE OFFSETS */
    postOffset: builder.mutation({
      query: (body: any) => ({
        url: "/offsets/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* EDIT OFFSETS BULK */
    putOffsetsBulk: builder.mutation({
      query: (body: any) => ({
        url: `/offsets/bulk`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetConfigurationFiles"],
    }),
    //* *********************************************************** */
    //* EDIT CONFIGURATION FILE */
    putConfigurationFile: builder.mutation({
      query: (body: any) => ({
        url: `/configurations/${body.id}`,
        method: "PUT",
        body: { id: body.id, ...body.body },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateConfiguration" },
      invalidatesTags: ["tagGetConfigurationFiles"],
    }),
    //* *********************************************************** */
    //* DELETE CONFIGURATION FILE */
    deleteConfigurationFile: builder.mutation({
      query: (body: any) => ({
        url: `/configurations/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetConfigurationFiles"],
    }),
    //* *********************************************************** */
    //* GET ALL SPECIAL SWITCHES */
    getAllSpecialSwitches: builder.query({
      query: (body: any) => ({
        url: `/special-definitions/`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetSpecialSwitches"],
    }),
    //* *********************************************************** */
    //* CREATE SPECIAL SWITCH */
    postSpecialSwitch: builder.mutation({
      query: (body: any) => ({
        url: `/special-definitions/`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetSpecialSwitches"],
    }),
    //* *********************************************************** */
    //* EDIT SPECIAL SWTICH */
    putSpecialSwitch: builder.mutation({
      query: (body: any) => ({
        url: `/special-definitions/${body.id}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetSpecialSwitches"],
    }),
    //* *********************************************************** */
    //* DELETE SPECIAL SWITCH */
    deleteSpecialSwitch: builder.mutation({
      query: (body: any) => ({
        url: `/special-definitions/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetSpecialSwitches"],
    }),
    //* *********************************************************** */
    //* GET ALL SPECIAL SWITCHES CATALOG */
    getAllSpecialSwitchesCatalog: builder.query({
      query: (body: any) => ({
        url: `/special-catalogs/`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetSpecialSwitchesCatalog"],
    }),
    //* *********************************************************** */
    //* CREATE SPECIAL SWITCH CATALOG */
    postSpecialSwitchCatalog: builder.mutation({
      query: (body: any) => ({
        url: "/special-catalogs/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetSpecialSwitchesCatalog"],
    }),
    //* *********************************************************** */
    //* EDIT SPECIAL SWTICH CATALOG */
    putSpecialSwitchCatalog: builder.mutation({
      query: (body: any) => ({
        url: `/special-catalogs/${body.id}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetSpecialSwitchesCatalog"],
    }),
    //* *********************************************************** */
    //* DELETE SPECIAL SWITCH CATALOG */
    deleteSpecialSwitchCatalog: builder.mutation({
      query: (body: any) => ({
        url: `/special-catalogs/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetSpecialSwitchesCatalog"],
    }),
    //* *********************************************************** */
    //* GET ALL DATA BLOCKS */
    getAllDataBlocks: builder.query({
      query: (body: any) => ({
        url: `/data-blocks/`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetDataBlocks"],
    }),
    //* *********************************************************** */
    //* GET DATA BLOCK */
    getDataBlock: builder.query({
      query: (body: any) => ({
        url: `/data-blocks/${body.dataBlockID}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetDataBlocks"],
    }),
    //* *********************************************************** */
    //* CREATE DATA BLOCK */
    postDataBlock: builder.mutation({
      query: (body: any) => ({
        url: "/data-blocks/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetDataBlocks"],
    }),
    //* *********************************************************** */
    //* EDIT DATA BLOCK */
    putDataBlock: builder.mutation({
      query: (body: any) => ({
        url: `/data-blocks/${body.id}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetDataBlocks"],
    }),
    //* *********************************************************** */
    //* DELETE DATA BLOCK */
    deleteDataBlock: builder.mutation({
      query: (body: any) => ({
        url: `/data-blocks/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetDataBlocks"],
    }),
    //* *********************************************************** */
    //* GET ALL VENDORS */
    getVendors: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        catalogue(
          fetchAllPages(_baseQuery, `/vendors/?&page=1&limit=500`, {
            authorization: body.token,
          }),
          catalogRow,
        ),
      providesTags: ["tagGetVendors"],
    }),
    //* *********************************************************** */
    //* CREATE VENDOR */
    postVendor: builder.mutation({
      query: (body: any) => ({
        url: "/vendors/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateVendor" },
      invalidatesTags: ["tagGetVendors"],
    }),
    //* *********************************************************** */
    //* EDIT VENDOR */
    putVendor: builder.mutation({
      query: (body: any) => ({
        url: `/vendors/${body.id}`,
        method: "PUT",
        body: { id: body.id, ...body.body },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateVendor" },
      invalidatesTags: ["tagGetVendors"],
    }),
    //* *********************************************************** */
    //* DELETE VENDOR */
    deleteVendor: builder.mutation({
      query: (body: any) => ({
        url: `/vendors/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetVendors"],
    }),
    //* *********************************************************** */
    //* GET ALL DATA BLOCK */
    getDataBlocks: builder.query({
      query: (body: any) => ({
        url: `/data-blocks`,
        headers: { authorization: body.token },
      }),
      providesTags: [],
    }),
    //* *********************************************************** */
    //* CREATE DATA BLOCK */
    postDataBlockComplete: builder.mutation({
      query: (body: any) => ({
        url: "/data-blocks/complete",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetConfigurationFiles"],
    }),
    //* *********************************************************** */
    //* EDIT DATA BLOCK OFFSET TYPE */
    putDataBlockComplete: builder.mutation({
      query: (body: any) => ({
        url: `/data-blocks/complete`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetConfigurationFiles"],
    }),
    //* *********************************************************** */
    //* GET ALL DATA BLOCK TYPES */
    getDataBlockTypes: builder.query({
      // Every page: LiftCloud returns 10 rows unless asked for more.
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        catalogue(
          fetchAllPages(_baseQuery, `/data-block-type/?page=1&limit=500`, {
            authorization: body.token,
          }),
          catalogRow,
        ),
      providesTags: ["tagGetDataBlocksTypes"],
    }),
    //* *********************************************************** */
    //* CREATE DATA BLOCK TYPES */
    postDataBlockTypes: builder.mutation({
      query: (body: any) => ({
        url: "/data-block-type/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetDataBlocksTypes"],
    }),
    //* *********************************************************** */
    //* EDIT DATA BLOCK TYPES */
    putDataBlockTypes: builder.mutation({
      query: (body: any) => ({
        url: `/data-block-type/${body.id}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetDataBlocksTypes"],
    }),
    //* *********************************************************** */
    //* DELETE DATA BLOCK TYPES */
    deleteDataBlockTypes: builder.mutation({
      query: (body: any) => ({
        url: `/data-block-type/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetDataBlocksTypes"],
    }),
    //* *********************************************************** */
  }),
});

export const {
  useLazyGetConfigurationQuery,
  useLazyGetConfigurationFilesQuery,
  useLazyGetAllOffsetsTypesQuery,
  usePostOffsetTypeMutation,
  useLazyGetOffsetsByDataBlockQuery,
  usePutOffsetTypeMutation,
  useDeleteOffsetTypeMutation,
  usePutOffsetsBulkMutation,
  usePostConfigurationFileMutation,
  usePostConfigurationMutation,
  usePostDefinitionMutation,
  usePutDefinitionMutation,
  useDeleteDefinitionMutation,
  usePostOffsetMutation,
  usePutConfigurationFileMutation,
  useDeleteConfigurationFileMutation,
  useLazyGetAllSpecialSwitchesCatalogQuery,
  usePostSpecialSwitchCatalogMutation,
  usePutSpecialSwitchCatalogMutation,
  useDeleteSpecialSwitchCatalogMutation,
  useLazyGetAllSpecialSwitchesQuery,
  usePostSpecialSwitchMutation,
  usePutSpecialSwitchMutation,
  useDeleteSpecialSwitchMutation,
  useLazyGetAllDataBlocksQuery,
  useLazyGetDataBlockQuery,
  usePostDataBlockMutation,
  usePutDataBlockMutation,
  useDeleteDataBlockMutation,
  useLazyGetVendorsQuery,
  usePostVendorMutation,
  usePutVendorMutation,
  useDeleteVendorMutation,
  useLazyGetDataBlocksQuery,
  usePostDataBlockCompleteMutation,
  usePutDataBlockCompleteMutation,
  useLazyGetDataBlockTypesQuery,
  usePostDataBlockTypesMutation,
  usePutDataBlockTypesMutation,
  useDeleteDataBlockTypesMutation,
  useDeleteOffsetTypeByDataBlockMutation,
} = configurationFileAPI;
